/**
 * usePedidoAutoPrintWatcher — Watcher global de pedidos de tienda (impresión + avisos).
 *
 * QUÉ HACE:
 *  - Montado en MainLayout: pollea tiendaService.getPedidos() cada 10 s (usePolling,
 *    pausa con tab oculto y refresco al volver), sin importar la ruta activa.
 *  - Detecta pedidos NUEVOS comparando los IDs ya vistos en la sesión (useRef) +
 *    la cola persistente (idempotencia entre sesiones: una entrada "impreso"
 *    jamás se reimprime).
 *  - AVISOS (independientes de la impresión): por cada pedido activo nuevo llama
 *    al aviso compartido `notifyOrderAlert` (beep + voz + notificación según
 *    «Sonidos del POS»): pedidos «local» → permiso de Caja; domicilio → permiso
 *    de Tienda. Al abrir la app solo siembra los IDs (no suena lo viejo).
 *  - IMPRESIÓN: dispara tryPrintThermalPedido solo para pedidos «local», sin
 *    abrir modales y sin window.print().
 *  - Sin transporte → cola "pendiente"; fallo → cola "fallo" con reintento por
 *    backoff. Si llega un pedido nuevo y no hay impresora disponible, dispara la
 *    compuerta global (printerGateStore → PrinterUnavailableModal, una vez por
 *    pedido) con acceso a la configuración POS; el pedido queda en cola hasta
 *    configurar la vía.
 *  - Con el toggle de auto-impresión apagado no imprime ni encola, pero los
 *    avisos siguen funcionando: memoriza los pedidos actuales para que al
 *    reactivarlo no se impriman de golpe los que ya existían.
 *
 * QUÉ EXPORTA: usePedidoAutoPrintWatcher
 *
 * QUÉ NO DEBE HACER:
 *  - No renderiza UI propia (la compuerta "sin impresora" la muestra
 *    PrinterUnavailableModal; el aviso de pedido nuevo es best-effort).
 *  - No usa localStorage directo ni fetch: todo pasa por services.
 */
import { useCallback, useRef } from "react";
import { useAuthStore } from "@/store/authStore";
import { usePermissions, usePolling, useStorageSignal } from "@/hooks";
import { getAppRoute } from "@/router/routeCatalog";
import { tiendaService } from "../services/tienda.service";
import { ESTADOS_PEDIDOS_ACTIVOS } from "../constants/pedidoEstados";
import {
  getPedidoAutoPrintPrefs,
  getPedidoAutoPrintQueue,
  prunePedidoImpresionQueue,
  shouldRetryFallo,
  upsertPedidoImpresion,
} from "../services/pedidoAutoPrint.service";
import {
  canPrintOnCurrentPlatform,
  isPrinterUnavailable,
  tryPrintThermalPedido,
} from "@/lib/native/posHardware";
import { askPrintWithoutPrinter } from "@/store/printerGateStore";
import { notifyOrderAlert } from "@/lib/alerts/orderAlerts";
import type { Pedido } from "@/types/tienda";

/** Frecuencia de detección: el requerimiento exige ≤ 15 s. */
const REFRESH_MS = 10_000;

/** Watcher global: no devuelve estado; solo orquesta detección + avisos + impresión. */
export const usePedidoAutoPrintWatcher = (): void => {
  const business = useAuthStore((s) => s.business);
  const businessId = business?.id ?? "default";
  const { canAccess } = usePermissions();
  const cajaRoute = getAppRoute("/caja");
  const tiendaRoute = getAppRoute("/tienda/pedidos");
  const canAlertLocal =
    !!cajaRoute && canAccess(cajaRoute.roles, cajaRoute.module);
  const canAlertDomicilio =
    !!tiendaRoute && canAccess(tiendaRoute.roles, tiendaRoute.module);

  // IDs ya vistos para avisos y para impresión (independientes entre sí).
  const seenAlertRef = useRef<Set<string>>(new Set());
  const seenPrintRef = useRef<Set<string>>(new Set());
  // Serializa la impresión: nunca dos intentos simultáneos.
  const printingRef = useRef(false);
  // Primer tick: siembra IDs actuales + reintenta cola (no avisa ni imprime pedidos viejos).
  const firstTickRef = useRef(true);

  /** Intenta imprimir un pedido y actualiza la cola según el resultado. */
  const intentarImprimir = useCallback(
    async (pedido: Pedido) => {
      if (!canPrintOnCurrentPlatform()) {
        // Sin transporte: queda en cola y se reintenta al configurar la vía.
        upsertPedidoImpresion(businessId, pedido.id, "pendiente", {
          intentos: 0,
        });
        return;
      }
      const prev = getPedidoAutoPrintQueue(businessId).find(
        (e) => e.pedidoId === pedido.id,
      );
      try {
        const ok = await tryPrintThermalPedido(pedido);
        upsertPedidoImpresion(businessId, pedido.id, ok ? "impreso" : "fallo", {
          intentos: ok ? 0 : (prev?.intentos ?? 0) + 1,
          ultimoIntento: new Date().toISOString(),
          ultimoError: ok
            ? null
            : "Impresión térmica no disponible (revisa la impresora o el relay)",
        });
      } catch (err) {
        upsertPedidoImpresion(businessId, pedido.id, "fallo", {
          intentos: (prev?.intentos ?? 0) + 1,
          ultimoIntento: new Date().toISOString(),
          ultimoError: String(err),
        });
      }
    },
    [businessId],
  );

  /** Pedido local nuevo detectado: compuerta sin impresora + intento de impresión. */
  const procesarPedidoNuevo = useCallback(
    async (pedido: Pedido) => {
      // Compuerta global una vez por pedido: si no hay impresora, el ticket
      // quedará en cola y no saldrá hasta configurar la vía de impresión.
      if (isPrinterUnavailable()) {
        void askPrintWithoutPrinter("el recibo del pedido");
      }
      await intentarImprimir(pedido);
    },
    [intentarImprimir],
  );

  /** Reintenta la cola: "pendiente" cuando ya hay transporte; "fallo" con backoff. */
  const procesarCola = useCallback(
    async (activos: Pedido[]) => {
      const cola = getPedidoAutoPrintQueue(businessId);
      for (const entry of cola) {
        if (entry.estadoImpresion === "impreso") continue;
        const pedido = activos.find((p) => p.id === entry.pedidoId);
        if (!pedido) continue;
        if (entry.estadoImpresion === "pendiente") {
          if (!canPrintOnCurrentPlatform()) continue;
          await intentarImprimir(pedido);
        } else if (shouldRetryFallo(entry, new Date())) {
          await intentarImprimir(pedido);
        }
      }
    },
    [businessId, intentarImprimir],
  );

  /** Un tick del watcher: avisos de pedidos nuevos, reintento de cola e impresión. */
  const tick = useCallback(async () => {
    if (printingRef.current) return;
    printingRef.current = true;
    try {
      let pedidos: Pedido[];
      try {
        pedidos = await tiendaService.getPedidos();
      } catch {
        return; // offline: el siguiente tick reintenta
      }

      const activosTienda = pedidos.filter((p) =>
        ESTADOS_PEDIDOS_ACTIVOS.includes(p.estado),
      );
      const activosLocal = activosTienda.filter(
        (p) => p.tipoEntrega === "local",
      );

      const prefs = getPedidoAutoPrintPrefs(businessId);
      const esPrimerTick = firstTickRef.current;
      firstTickRef.current = false;

      // Primer tick: siembra los IDs actuales (no son "nuevos") y solo
      // reintenta la cola existente. Nunca avisa ni imprime pedidos viejos.
      if (esPrimerTick) {
        for (const p of activosTienda) seenAlertRef.current.add(p.id);
        for (const p of activosLocal) seenPrintRef.current.add(p.id);
        await procesarCola(activosLocal);
        return;
      }

      // Avisos (sonido + voz + notificación), independientes de la impresión.
      for (const pedido of activosTienda) {
        if (seenAlertRef.current.has(pedido.id)) continue;
        seenAlertRef.current.add(pedido.id);
        const esLocal = pedido.tipoEntrega === "local";
        if (esLocal ? canAlertLocal : canAlertDomicilio) {
          notifyOrderAlert(esLocal ? "local" : "domicilio", {
            detalle: pedido.cliente.nombre,
            pedidoId: pedido.id,
            screen: esLocal ? "caja" : "tienda/pedidos",
          });
        }
      }

      // Con auto-impresión apagada no se encola ni imprime nada: solo se
      // memorizan los pedidos actuales para que al reactivarla no se
      // impriman de golpe los que ya existían.
      if (!prefs.autoPrintEnabled) {
        for (const p of activosLocal) seenPrintRef.current.add(p.id);
        return;
      }

      // Limpia entradas de pedidos que ya pasaron a estado terminal.
      prunePedidoImpresionQueue(businessId, activosLocal);

      await procesarCola(activosLocal);

      for (const pedido of activosLocal) {
        if (seenPrintRef.current.has(pedido.id)) continue;
        seenPrintRef.current.add(pedido.id);
        // Idempotencia entre sesiones: ya impreso → no repetir.
        const prev = getPedidoAutoPrintQueue(businessId).find(
          (e) => e.pedidoId === pedido.id,
        );
        if (prev?.estadoImpresion === "impreso") continue;
        await procesarPedidoNuevo(pedido);
      }
    } finally {
      printingRef.current = false;
    }
  }, [
    businessId,
    canAlertDomicilio,
    canAlertLocal,
    procesarCola,
    procesarPedidoNuevo,
  ]);

  usePolling(tick, { interval: REFRESH_MS, runImmediately: true });

  // Re-acciona al instante cuando otra pestaña crea/actualiza pedidos (local).
  useStorageSignal("tienda_pedidos", () => {
    void tick();
  });
};
